import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Grid,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Divider,
    Paper
} from '@mui/material';
import {
    Close as CloseIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Download as DownloadIcon,
    Publish as PublishIcon,
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    Image as ImageIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import {
    updateContentReview,
    bulkDownloadContent,
    bulkPublishContent
} from '../../services/casService';
import { generateImageForContent, getImageDataUrl } from '../../services/imageGenerationService';

// AuthenticatedImage component for displaying images with authentication
const AuthenticatedImage = ({ contentId, className, style }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchImage = async () => {
            try {
                console.log('AuthenticatedImage: Fetching image for contentId:', contentId);
                setIsLoading(true);
                const dataUrl = await getImageDataUrl(contentId);
                console.log('AuthenticatedImage: Received data URL:', dataUrl ? 'Present' : 'Missing');
                setImageUrl(dataUrl);
            } catch (error) {
                console.error('AuthenticatedImage: Error fetching image:', error);
                if (error.response?.status === 404) {
                    setError('No image available');
                } else if (error.response?.status === 400) {
                    setError('Image not ready yet');
                } else {
                    setError('Failed to load image');
                }
            } finally {
                setIsLoading(false);
            }
        };

        if (contentId) {
            fetchImage();
        }
    }, [contentId]);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!imageUrl) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography color="text.secondary">No image available</Typography>
            </Box>
        );
    }

    return (
        <img
            src={imageUrl}
            alt="Generated content image"
            className={className}
            style={style}
            onError={() => setError('Failed to load image')}
        />
    );
};

const RewrittenContentViewer = ({
    content,
    onClose,
    onReview,
    onReject,
    hasImage,
    onNavigate,
    currentIndex = 0,
    totalItems = 0,
    propertyId = null,
    isFromContentReview = false
}) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editedContent, setEditedContent] = useState({
        edited_title: content?.edited_title || content?.rewritten_title || '',
        edited_meta_description: content?.edited_meta_description || content?.rewritten_meta_description || '',
        edited_h1: content?.edited_h1 || content?.rewritten_h1 || '',
        edited_body_content: content?.edited_body_content || content?.rewritten_body_content || '',
        edit_notes: content?.edit_notes || ''
    });

    useEffect(() => {
        if (content) {
            setEditedContent({
                edited_title: content.edited_title || content.rewritten_title || '',
                edited_meta_description: content.edited_meta_description || content.rewritten_meta_description || '',
                edited_h1: content.edited_h1 || content.rewritten_h1 || '',
                edited_body_content: content.edited_body_content || content.rewritten_body_content || '',
                edit_notes: content.edit_notes || ''
            });
        }
    }, [content]);

    const handleGenerateImage = async () => {
        setIsGenerating(true);
        try {
            await generateImageForContent(content.id);
            // Trigger content refresh to get updated image status
            window.dispatchEvent(new CustomEvent('refreshContent'));
            if (onReview) {
                onReview(content, 'Image generation initiated successfully');
            }
        } catch (error) {
            console.error('Failed to generate image:', error);
            if (onReview) {
                onReview(content, 'Failed to generate image');
            }
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDownloadZip = async () => {
        try {
            const blob = await bulkDownloadContent([content.id], 'txt');
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `content-${content.id}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading zip:', error);
        }
    };

    const handleSaveEdit = async () => {
        setIsSaving(true);
        try {
            if (isFromContentReview && propertyId) {
                await updateContentReview(propertyId, content.id, {
                    edited_title: editedContent.edited_title,
                    edited_meta_description: editedContent.edited_meta_description,
                    edited_h1: editedContent.edited_h1,
                    edited_body_content: editedContent.edited_body_content,
                    edit_notes: editedContent.edit_notes
                });
            }
            setIsEditing(false);
            if (onReview) {
                onReview(content, 'Content updated successfully');
            }
        } catch (error) {
            console.error('Error saving edited content:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleReview = async () => {
        try {
            if (isFromContentReview && propertyId) {
                await updateContentReview(propertyId, content.id, {
                    review_status: 'reviewed',
                    review_notes: reviewNotes || 'Reviewed via Content Viewer'
                });
            }
            if (onReview) {
                onReview(content, reviewNotes);
            }
        } catch (error) {
            console.error('Error reviewing content:', error);
        }
    };

    const handleReject = async () => {
        try {
            if (isFromContentReview && propertyId) {
                await updateContentReview(propertyId, content.id, {
                    review_status: 'rejected',
                    review_notes: 'Rejected via Content Viewer'
                });
            }
            if (onReject) {
                onReject(content);
            }
        } catch (error) {
            console.error('Error rejecting content:', error);
        }
    };

    const handlePublish = async () => {
        try {
            await bulkPublishContent([content.id], propertyId, 'wordpress');
            if (onReview) {
                onReview(content, 'Content published successfully');
            }
        } catch (error) {
            console.error('Error publishing content:', error);
        }
    };

    const handleCancelEdit = () => {
        setEditedContent({
            edited_title: content.edited_title || content.rewritten_title || '',
            edited_meta_description: content.edited_meta_description || content.rewritten_meta_description || '',
            edited_h1: content.edited_h1 || content.rewritten_h1 || '',
            edited_body_content: content.edited_body_content || content.rewritten_body_content || '',
            edit_notes: content.edit_notes || ''
        });
        setIsEditing(false);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            'generated_pending_pickup': { label: 'Pending Pickup', color: 'warning' },
            'downloaded': { label: 'Downloaded', color: 'info' },
            'reviewed': { label: 'Reviewed', color: 'success' },
            'published': { label: 'Published', color: 'secondary' },
            'feedback_rejected': { label: 'Rejected', color: 'error' }
        };

        const config = statusConfig[status] || { label: status || 'Unknown', color: 'default' };

        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
                variant="outlined"
            />
        );
    };

    const getContentTypeBadge = (contentType) => {
        const typeConfig = {
            'page_rewrite': { label: 'Page Rewrite', color: 'success' },
            'blog': { label: 'Blog Post', color: 'secondary' }
        };

        const config = typeConfig[contentType] || { label: contentType || 'Unknown', color: 'default' };

        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
                variant="outlined"
            />
        );
    };

    if (!content) return null;

    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                style: { maxHeight: '90vh' }
            }}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h6">
                            {content.rewritten_title || content.page_path || 'Content Viewer'}
                        </Typography>
                        {getContentTypeBadge(content.content_type || 'page_rewrite')}
                        {getStatusBadge(content.status || 'generated_pending_pickup')}
                    </Box>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    {/* Navigation */}
                    {onNavigate && totalItems > 1 && (
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Button
                                startIcon={<PrevIcon />}
                                onClick={() => onNavigate('prev')}
                                disabled={currentIndex === 0}
                            >
                                Previous
                            </Button>
                            <Typography variant="body2" color="text.secondary">
                                {currentIndex + 1} of {totalItems}
                            </Typography>
                            <Button
                                endIcon={<NextIcon />}
                                onClick={() => onNavigate('next')}
                                disabled={currentIndex === totalItems - 1}
                            >
                                Next
                            </Button>
                        </Box>
                    )}

                    {/* Action Buttons */}
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadZip}
                        >
                            Download Zip
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ApproveIcon />}
                            onClick={handleReview}
                        >
                            Review
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<RejectIcon />}
                            onClick={handleReject}
                            color="error"
                        >
                            Reject
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<PublishIcon />}
                            onClick={handlePublish}
                            color="secondary"
                        >
                            Publish
                        </Button>
                        {!hasImage && (
                            <Button
                                variant="outlined"
                                startIcon={isGenerating ? <CircularProgress size={16} /> : <ImageIcon />}
                                onClick={handleGenerateImage}
                                disabled={isGenerating}
                                color="primary"
                            >
                                {isGenerating ? 'Generating...' : 'Generate Image'}
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={isEditing ? <CancelIcon /> : <EditIcon />}
                            onClick={() => setIsEditing(!isEditing)}
                        >
                            {isEditing ? 'Cancel Edit' : 'Edit Content'}
                        </Button>
                    </Box>

                    {/* Review Notes */}
                    <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Review Notes"
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder="Add review notes..."
                        sx={{ mb: 2 }}
                    />
                </Box>

                {/* Property Information */}
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
                    <Typography variant="h6" gutterBottom>Property Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                Property: {content.managed_property?.friendly_name || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                URL: {content.page_url || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                Path: {content.page_path || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                Created: {content.created_at ? format(new Date(content.created_at), 'MMM dd, yyyy HH:mm') : 'N/A'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Content Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Content</Typography>

                    {/* Title */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Title {content.edited_title && <Chip label="Edited" size="small" color="primary" />}
                        </Typography>
                        {isEditing ? (
                            <TextField
                                fullWidth
                                value={editedContent.edited_title}
                                onChange={(e) => setEditedContent({ ...editedContent, edited_title: e.target.value })}
                                variant="outlined"
                            />
                        ) : (
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography>{content.edited_title || content.rewritten_title}</Typography>
                            </Paper>
                        )}
                    </Box>

                    {/* H1 Tag */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            H1 Tag {content.edited_h1 && <Chip label="Edited" size="small" color="primary" />}
                        </Typography>
                        {isEditing ? (
                            <TextField
                                fullWidth
                                value={editedContent.edited_h1}
                                onChange={(e) => setEditedContent({ ...editedContent, edited_h1: e.target.value })}
                                variant="outlined"
                            />
                        ) : (
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography>{content.edited_h1 || content.rewritten_h1}</Typography>
                            </Paper>
                        )}
                    </Box>

                    {/* Meta Description */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Meta Description {content.edited_meta_description && <Chip label="Edited" size="small" color="primary" />}
                        </Typography>
                        {isEditing ? (
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                value={editedContent.edited_meta_description}
                                onChange={(e) => setEditedContent({ ...editedContent, edited_meta_description: e.target.value })}
                                variant="outlined"
                            />
                        ) : (
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Typography>{content.edited_meta_description || content.rewritten_meta_description}</Typography>
                            </Paper>
                        )}
                    </Box>

                    {/* Body Content */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Body Content {content.edited_body_content && <Chip label="Edited" size="small" color="primary" />}
                        </Typography>
                        {isEditing ? (
                            <TextField
                                fullWidth
                                multiline
                                rows={15}
                                value={editedContent.edited_body_content}
                                onChange={(e) => setEditedContent({ ...editedContent, edited_body_content: e.target.value })}
                                variant="outlined"
                            />
                        ) : (
                            <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: '400px', overflow: 'auto' }}>
                                <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                                    {content.edited_body_content || content.rewritten_body_content}
                                </Typography>
                            </Paper>
                        )}
                    </Box>

                    {/* Edit Notes */}
                    {content.edit_notes && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Edit Notes</Typography>
                            <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
                                <Typography>{content.edit_notes}</Typography>
                            </Paper>
                        </Box>
                    )}

                    {/* Save/Cancel buttons for editing */}
                    {isEditing && (
                        <Box display="flex" gap={1} mt={2}>
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveEdit}
                                disabled={isSaving}
                            >
                                {isSaving ? <CircularProgress size={20} /> : 'Save Changes'}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<CancelIcon />}
                                onClick={handleCancelEdit}
                            >
                                Cancel
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* Image Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Generated Image</Typography>

                    {!hasImage ? (
                        <Paper sx={{ p: 3, bgcolor: 'grey.50', textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                No image has been generated for this content yet.
                                Click the "Generate Image" button above to create an AI-powered image.
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<ImageIcon />}
                                onClick={handleGenerateImage}
                                disabled={isGenerating}
                            >
                                {isGenerating ? 'Generating...' : 'Generate Image'}
                            </Button>
                        </Paper>
                    ) : (
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Box display="flex" justifyContent="center">
                                <AuthenticatedImage
                                    contentId={content.id}
                                    style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '8px' }}
                                />
                            </Box>
                        </Paper>
                    )}
                </Box>

                {/* Image Guidance Section */}
                {content.image_guidance && content.image_guidance.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>Image Guidance</Typography>
                        <Paper sx={{ p: 2, bgcolor: 'info.50' }}>
                            <Grid container spacing={2}>
                                {content.image_guidance.map((image, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Box sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                {image.type || `Image ${index + 1}`}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {image.description}
                                            </Typography>
                                            {image.alt_text && (
                                                <Box sx={{ mb: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Alt Text:
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {image.alt_text}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {image.placement && (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Placement:
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {image.placement}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Box>
                )}

                {/* SEO Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>SEO Information</Typography>

                    {/* Focus Keyword */}
                    {content.focus_keyword && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Focus Keyword</Typography>
                            <Chip label={content.focus_keyword} color="primary" />
                        </Box>
                    )}

                    {/* Secondary Keywords */}
                    {content.secondary_keywords && content.secondary_keywords.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom>Secondary Keywords</Typography>
                            <Box display="flex" gap={1} flexWrap="wrap">
                                {content.secondary_keywords.map((keyword, index) => (
                                    <Chip key={index} label={keyword} size="small" color="success" variant="outlined" />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Content Metrics */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Content Metrics</Typography>
                        <Grid container spacing={2}>
                            {content.estimated_word_count && (
                                <Grid item xs={12} md={4}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h4" color="primary">
                                            {content.estimated_word_count}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Estimated Words
                                        </Typography>
                                    </Paper>
                                </Grid>
                            )}
                            {content.readability_score_target && (
                                <Grid item xs={12} md={4}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h4" color="success.main">
                                            {content.readability_score_target}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Readability Score
                                        </Typography>
                                    </Paper>
                                </Grid>
                            )}
                            {content.keyword_density_target && (
                                <Grid item xs={12} md={4}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h4" color="secondary.main">
                                            {content.keyword_density_target}%
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Keyword Density
                                        </Typography>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RewrittenContentViewer; 